import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Download, FileDown, Settings2, Languages } from "lucide-react";
import PageHeader from "@/components/app/PageHeader";
import PremiumSheets from "@/components/app/PremiumSheets";
import { triggerDownload } from "@/lib/premium";

const PREMADE_TINTS = {
  "hiragana-full": "bg-sakura/40", "hiragana-vowels": "bg-yuzu/50", "katakana-full": "bg-ramune/40",
  "kanji-n5": "bg-matcha/50", "kanji-n4": "bg-lavender/50", "kanji-n3": "bg-peach/40",
};

function LangToggle({ pdfLang, setPdfLang, t }) {
  return (
    <div className="kawaii-card p-2 pl-4 flex flex-wrap items-center gap-3" data-testid="pdf-lang-toggle">
      <Languages className="w-4 h-4 text-ink-soft" strokeWidth={2.5} />
      <div className="text-sm font-extrabold text-ink mr-1">{t("pdfLangLabel")}</div>
      <div className="inline-flex gap-1">
        <button data-testid="pdf-lang-en" onClick={() => setPdfLang("en")} className={`kawaii-pill-nav !text-xs ${pdfLang === "en" ? "active" : ""}`}>English</button>
        <button data-testid="pdf-lang-pt" onClick={() => setPdfLang("pt")} className={`kawaii-pill-nav !text-xs ${pdfLang === "pt" ? "active" : ""}`}>Português</button>
      </div>
    </div>
  );
}

function CustomBuilder({ pdfLang, t }) {
  const [system, setSystem] = useState("hiragana");
  const [level, setLevel] = useState("N5");
  const [chars, setChars] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [columns, setColumns] = useState(10);
  const [rows, setRows] = useState(1);
  const [guide, setGuide] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const url = system === "kanji" ? `/characters/kanji?level=${level}` : `/characters/${system}`;
    api.get(url).then(({ data }) => { setChars(data); setPicked(new Set()); });
  }, [system, level]);

  const toggle = (ch) => { const s = new Set(picked); s.has(ch) ? s.delete(ch) : s.add(ch); setPicked(s); };

  const downloadCustom = async () => {
    setBusy(true);
    try {
      const body = { system, level: system === "kanji" ? level : undefined, characters: picked.size ? Array.from(picked) : null, columns, rows_per_char: rows, include_guide: guide, lang: pdfLang, title: null };
      const res = await fetch(`${API}/pdf/practice`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error("Failed");
      triggerDownload(await res.blob(), `custom_${system}.pdf`);
    } catch { toast.error("Failed to generate PDF"); }
    finally { setBusy(false); }
  };

  const previewChars = (picked.size ? chars.filter((c) => picked.has(c.char)) : chars).slice(0, 3);

  return (
    <section>
      <div className="mb-5 flex items-center gap-2">
        <Settings2 className="w-5 h-5 text-ink-soft" strokeWidth={2.5} />
        <h2 className="font-display text-2xl text-ink">{t("buildYourOwn")}</h2>
      </div>
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="kawaii-card p-6 space-y-5">
          <div>
            <label className="eyebrow !text-ink-soft">{t("system")}</label>
            <div className="kawaii-card inline-flex gap-1 p-1 mt-2">
              {["hiragana", "katakana", "kanji"].map((s) => (
                <button key={s} data-testid={`pdf-system-${s}`} onClick={() => setSystem(s)} className={`kawaii-pill-nav !text-xs capitalize ${system === s ? "active" : ""}`}>{s}</button>
              ))}
            </div>
          </div>
          {system === "kanji" && (
            <div>
              <label className="eyebrow !text-ink-soft">{t("jlptLevel")}</label>
              <div className="kawaii-card inline-flex gap-1 p-1 mt-2">
                {["N5", "N4", "N3"].map((lv) => (
                  <button key={lv} data-testid={`pdf-level-${lv}`} onClick={() => setLevel(lv)} className={`kawaii-pill-nav !text-xs ${level === lv ? "active" : ""}`}>{lv}</button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="eyebrow !text-ink-soft">{t("columnsLabel")}: {columns}</label>
            <input data-testid="pdf-columns" type="range" min="6" max="12" value={columns} onChange={(e) => setColumns(parseInt(e.target.value))} className="w-full mt-2" />
          </div>
          <div>
            <label className="eyebrow !text-ink-soft">{t("rowsLabel")}: {rows}</label>
            <input data-testid="pdf-rows" type="range" min="1" max="4" value={rows} onChange={(e) => setRows(parseInt(e.target.value))} className="w-full mt-2" />
          </div>
          <label className="flex items-center gap-2 font-extrabold text-sm text-ink">
            <input data-testid="pdf-guide" type="checkbox" checked={guide} onChange={(e) => setGuide(e.target.checked)} className="w-5 h-5 accent-[#FF80AB] rounded" />
            {t("includeGuides")}
          </label>
          <button data-testid="generate-pdf-btn" onClick={downloadCustom} disabled={busy} className="kawaii-btn kawaii-btn-primary w-full">
            <Download className="w-4 h-4" strokeWidth={2.5} />
            {busy ? t("generating") : `${t("download")} · ${pdfLang.toUpperCase()} · ${picked.size || chars.length}`}
          </button>
        </div>

        <div className="kawaii-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="font-extrabold text-ink">{t("pickChars")} <span className="text-ink-soft font-semibold text-sm">{t("pickCharsSub")}</span></div>
            {picked.size > 0 && <button onClick={() => setPicked(new Set())} className="kawaii-btn kawaii-btn-sm kawaii-btn-ghost !text-xs">{t("clear")}</button>}
          </div>
          <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-2 max-h-96 overflow-auto pr-1">
            {chars.map((c) => {
              const on = picked.has(c.char);
              return (
                <button key={c.char} data-testid={`pdf-pick-${c.romaji || c.meaning}`} onClick={() => toggle(c.char)} title={c.romaji || c.meaning}
                  className={`aspect-square rounded-xl flex items-center justify-center transition-all duration-200 border ${on ? "bg-ink text-white border-ink shadow-soft-lg scale-105" : "bg-white/80 border-white hover:bg-sakura/20"}`}>
                  <span className="font-jp text-2xl">{c.char}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-8">
        <div className="eyebrow !text-ink-soft mb-3">{t("preview")}</div>
        <div className="kawaii-card !bg-[#FFF3F6] p-6 overflow-hidden" data-testid="pdf-preview">
          <div className="space-y-4">
            {previewChars.slice(0, 3).map((c) => (
              <div key={c.char}>
                <div className="text-[10px] font-extrabold uppercase text-sakura-deep mb-1">{c.romaji || c.meaning}</div>
                <div className="flex gap-1 items-start overflow-x-auto">
                  <div className="genko-cell genko-ref mr-1"><span className="font-jp text-3xl text-ink">{c.char}</span></div>
                  {Array.from({ length: columns }).map((_, i) => (
                    <div key={i} className="genko-cell shrink-0">{guide && i < 3 && <span className="font-jp text-3xl text-ink/25">{c.char}</span>}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function PDFPage() {
  const { lang, t } = useLang();
  const [pdfLang, setPdfLang] = useState(lang);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPdfLang(lang); }, [lang]);

  const PREMADE = [
    { id: "hiragana-full", title: t("hiraganaFullTitle"), desc: t("hiraganaFullDesc") },
    { id: "hiragana-vowels", title: t("hiraganaVowelsTitle"), desc: t("hiraganaVowelsDesc") },
    { id: "katakana-full", title: t("katakanaFullTitle"), desc: t("katakanaFullDesc") },
    { id: "kanji-n5", title: t("kanjiN5Title"), desc: t("kanjiN5Desc") },
    { id: "kanji-n4", title: t("kanjiN4Title"), desc: t("kanjiN4Desc") },
    { id: "kanji-n3", title: t("kanjiN3Title"), desc: t("kanjiN3Desc") },
  ];

  const downloadPremade = async (id) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/pdf/premade/${id}?lang=${pdfLang}`);
      triggerDownload(await res.blob(), `${id}_${pdfLang}.pdf`);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <PageHeader eyebrow={t("printPractice")} title="Genkoyoushi PDFs" subtitle={t("genkoyoushiBody")}
        right={<LangToggle pdfLang={pdfLang} setPdfLang={setPdfLang} t={t} />} />

      <section className="mb-16">
        <h2 className="font-display text-2xl text-ink mb-5">{t("readyPrint")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PREMADE.map((p) => (
            <div key={p.id} className="kawaii-card kawaii-card-hover p-6 flex flex-col gap-4">
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl ${PREMADE_TINTS[p.id]}`}>
                <FileDown className="w-5 h-5 text-ink" strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <div className="font-display text-lg text-ink">{p.title}</div>
                <p className="text-sm text-ink-soft mt-1">{p.desc}</p>
              </div>
              <button data-testid={`download-${p.id}`} onClick={() => downloadPremade(p.id)} disabled={busy} className="kawaii-btn kawaii-btn-sm self-start">
                <Download className="w-4 h-4" strokeWidth={2.5} /> {t("download")} · {pdfLang.toUpperCase()}
              </button>
            </div>
          ))}
        </div>
      </section>

      <PremiumSheets pdfLang={pdfLang} />
      <CustomBuilder pdfLang={pdfLang} t={t} />
    </div>
  );
}
